import React from 'react';
import './ToggleSwitch.css';

export const MAX_CUSTOM_FLAGS = 50;
export const MAX_FLAG_KEY_LENGTH = 64;
export const MAX_FLAG_VALUE_LENGTH = 2000;
const FLAG_KEY_PATTERN = /^[A-Za-z0-9_.-]+$/;

export function mapFlagsToForm(flags) {
  if (!Array.isArray(flags)) return [];
  return flags.map(({ key, enabled, value }) => ({
    key: key || '',
    enabled: !!enabled,
    value: value || '',
  }));
}

export function normalizeCustomFlags(flags) {
  return (flags || []).map(({ key, enabled, value }) => ({
    key: (key || '').trim(),
    enabled: !!enabled,
    value: (value || '').trim(),
  }));
}

export function validateCustomFlags(flags) {
  if (flags.length > MAX_CUSTOM_FLAGS) return `Не більше ${MAX_CUSTOM_FLAGS} прапорців на додаток`;

  const seen = new Set();
  for (const [index, flag] of flags.entries()) {
    if (!flag.key) return `Прапорець №${index + 1}: вкажіть назву`;
    if (flag.key.length > MAX_FLAG_KEY_LENGTH) {
      return `Прапорець ${flag.key}: назва довша за ${MAX_FLAG_KEY_LENGTH} символи`;
    }
    if (!FLAG_KEY_PATTERN.test(flag.key)) {
      return `Прапорець ${flag.key}: у назві дозволені лише латинські літери, цифри, «_», «.» та «-»`;
    }
    if (flag.value.length > MAX_FLAG_VALUE_LENGTH) {
      return `Прапорець ${flag.key}: значення довше за ${MAX_FLAG_VALUE_LENGTH} символів`;
    }
    if (seen.has(flag.key)) return `Прапорець ${flag.key} повторюється`;
    seen.add(flag.key);
  }

  return '';
}

export default function CustomFlags({ flags, onChange, platform, appId }) {
  const updateFlag = (index, patch) =>
    onChange(flags.map((flag, position) => (position === index ? { ...flag, ...patch } : flag)));

  const removeFlag = (index) => onChange(flags.filter((_, position) => position !== index));

  const addFlag = () => onChange([...flags, { key: '', enabled: false, value: '' }]);

  const endpoint = `${window.location.origin}/api/flags/${platform}/${appId.trim() || '<appId>'}`;

  return (
    <div className="row g-3">
      <div className="col-12">
        <div className="app-editor-card compact-card">
          <div className="app-editor-card-body compact-card-body">
            <div className="cflags-head">
              <div>
                <div className="compact-side-title">Custom flags</div>
                <div className="cflags-hint">
                  Перемикач і рядок для розробників. Зміни набувають чинності після Save.
                </div>
              </div>
              <button
                type="button"
                className="fbtn"
                onClick={addFlag}
                disabled={flags.length >= MAX_CUSTOM_FLAGS}
              >
                Додати прапорець
              </button>
            </div>

            {flags.length ? (
              <div className="cflags-list">
                <div className="cflags-row cflags-row-head" aria-hidden="true">
                  <span>Назва</span>
                  <span>Значення</span>
                  <span>Увімкнено</span>
                  <span />
                </div>

                {flags.map((flag, index) => (
                  <div className="cflags-row" key={index}>
                    <input
                      type="text"
                      value={flag.key}
                      onChange={(e) => updateFlag(index, { key: e.target.value })}
                      placeholder="promo_banner"
                      maxLength={MAX_FLAG_KEY_LENGTH}
                      aria-label={`Назва прапорця №${index + 1}`}
                      className="form-control app-editor-input compact-input cflags-key"
                    />
                    <input
                      type="text"
                      value={flag.value}
                      onChange={(e) => updateFlag(index, { value: e.target.value })}
                      placeholder="рядок, необов’язково"
                      maxLength={MAX_FLAG_VALUE_LENGTH}
                      aria-label={`Значення прапорця №${index + 1}`}
                      className="form-control app-editor-input compact-input cflags-value"
                    />
                    <label className="switch mb-0 cflags-toggle" title={flag.enabled ? 'Увімкнено' : 'Вимкнено'}>
                      <input
                        type="checkbox"
                        checked={flag.enabled}
                        onChange={(e) => updateFlag(index, { enabled: e.target.checked })}
                        aria-label={`Увімкнути прапорець №${index + 1}`}
                      />
                      <span className="slider round"></span>
                    </label>
                    <button
                      type="button"
                      className="fbtn is-danger cflags-remove"
                      onClick={() => removeFlag(index)}
                    >
                      Видалити
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="cflags-empty">Прапорців ще немає</div>
            )}
          </div>
        </div>
      </div>

      <div className="col-12">
        <div className="app-editor-card compact-card">
          <div className="app-editor-card-body compact-card-body">
            <div className="compact-side-title">Запит для розробників</div>
            <div className="cflags-hint">
              Відповідь містить усі прапорці додатка, увімкнені й вимкнені. Ключ X-Flags-Key видає адміністратор.
            </div>
            <code className="cflags-endpoint">
              GET {endpoint}
              {'\n'}X-Flags-Key: &lt;ключ&gt;
            </code>
          </div>
        </div>
      </div>
    </div>
  );
}
